import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { dataService } from '../../services/DataService'

export const DesignDropdown = ({ value, onChange, className = '', disabled = false }) => {
  const { t } = useTranslation('lookup')
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [designs, setDesigns] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedDesign, setSelectedDesign] = useState(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)

  // Initialize selected design based on value
  useEffect(() => {
    if (value && designs.length > 0) {
      const design = designs.find(d => d.mserp_designname === value)
      setSelectedDesign(design)
    } else if (value) {
      // If we have a value but no matching design, create a placeholder
      setSelectedDesign({ mserp_design: '', mserp_designname: value })
    } else {
      setSelectedDesign(null)
    }
  }, [value, designs])

  // Debounced search with lazy loading
  useEffect(() => {
    if (searchTerm && searchTerm.length >= 2) {
      const timeoutId = setTimeout(() => {
        loadDesigns(searchTerm)
      }, 300) // 300ms debounce

      return () => clearTimeout(timeoutId)
    } else if (searchTerm === '') {
      // Load initial designs when dropdown opens without search
      loadDesigns('')
    }
  }, [searchTerm])

  const loadDesigns = async (search) => {
    setLoading(true)

    try {
      const result = await dataService.getDesigns(search, 100) // Get up to 100 designs

      if (result.success) {
        setDesigns(result.data)
      } else {
        console.error('Failed to load designs:', result.error)
        setDesigns([])
      }
    } catch (error) {
      console.error('Error loading designs:', error)
      setDesigns([])
    } finally {
      setLoading(false)
    }
  }

  // Close dropdown when clicking outside and handle position updates
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    const handleScroll = () => {
      if (isOpen) {
        calculateDropdownPosition()
      }
    }

    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleResize)

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleResize)
      }
    } else {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const calculateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()

      let top = rect.bottom + 4
      let left = rect.left
      const width = rect.width

      setDropdownPosition({ top, left, width })
    }
  }

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true)
      calculateDropdownPosition()
      // Always load designs on focus to show at least some results
      if (designs.length === 0 || searchTerm === '') {
        loadDesigns('')
      }
    }
  }

  const handleSelectDesign = (design) => {
    setSelectedDesign(design)
    onChange(design.mserp_designname) // Store the design name, not the code
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleClear = (e) => {
    e.stopPropagation()
    setSelectedDesign(null)
    onChange('')
    setSearchTerm('')
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
    if (!isOpen) {
      setIsOpen(true)
      calculateDropdownPosition()
    }
  }

  return (
    <div className={`relative ${className}`}>
      <div className="relative" ref={inputRef}>
        <input
          type="text"
          value={isOpen ? searchTerm : (value || '')}
          onChange={handleSearchChange}
          onFocus={handleInputFocus}
          onClick={handleInputFocus}
          disabled={disabled}
          placeholder={t('design.placeholder')}
          className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        {value && !disabled && (
          <button
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            type="button"
          >
            ×
          </button>
        )}
      </div>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-white border border-gray-300 rounded-sm shadow-lg max-h-60 overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 999999
          }}
        >
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500">{t('design.loading')}</div>
          ) : designs.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              {searchTerm ? t('design.noResults') : t('design.noDesigns')}
            </div>
          ) : (
            <ul className="py-1">
              {designs.map((design) => (
                <li
                  key={design.mserp_alg_portalquotationdesignsentityid || design.mserp_design}
                  onClick={() => handleSelectDesign(design)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  {design.mserp_designname && (
                    <div className="font-medium text-sm">{design.mserp_designname}</div>
                  )}
                  {design.mserp_design && (
                    <div className="text-xs text-gray-500">{design.mserp_design}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}
